import { useCallback, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useAtomValue } from 'jotai';
import { Box } from '@mui/material';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import SmsVerificationForm from '@/components/@IntranetLibrary/Form/SmsVerificationForm';
import { RadioGroupV2 } from '@/components/@IntranetLibrary/Radio/RadioV2';
import {
  useHtmlToReact,
  type ElementTransform,
} from '@/components/Common/HtmlToReact';
import InfoList from '@/components/Approval/@components/InfoList';
import {
  APPROVAL_SECTION_GAP_LG,
  InfoSectionTitle,
} from '@/components/Approval/@components/InfoSection';
import { EXPIRE_CERTIFY_CODE_URL } from '@/constant/apiList';
import useHasRoles from '@/hooks/useHasRoles';
import type { SmsVerification } from '@/hooks/form/useSmsVerificationForm';
import { MyInfoState } from '@/recoil/atom';
import NewWindowIcon from '@/public/icons/approval/new-window.svg';
import { fetchApi } from '@/utils/api/customAxios';
import { openDownloadUrlInNewTab } from '@/utils/approval/resolveDownloadUrl';
import {
  formatNewCardPhoneNumber,
  newCardCreditLimitLabel,
  type NewCorporateCardAttributes,
  type NewCorporateCardFormState,
} from '@/utils/approval/newCorporateCard';
import {
  AuthFileHint,
  AuthFileLink,
  AuthNotice,
  NewCardAgreeRow,
  NewCardPledgeBox,
} from './styles';

export {
  CardTypeOption,
  NewCardTextLink,
  NewCardAgreeAlert,
  NewCardAgreeRow,
  NewCardPledgeBox,
  RequiredSectionTitle,
  SectionHint,
  VerifiedText,
} from './styles';

/** 조회 화면 신분증·통장 열람 담당자 역할 */
export const CORPORATE_CARD_MANAGER_ROLE_KEY = 'corporate_card_manager';

/** 레거시 서약서 문구 — 원문 그대로(번역 없음) */
export const NEW_CARD_PLEDGE_TITLE =
  '본인은 개인형 법인카드 사용함에 있어 아래와 같이 서약합니다.';

export const NEW_CARD_PLEDGE_ITEMS = [
  '본인은 법인카드 관련된 내부규정을 준수하며, 미정산 혹은 개인적인 사용분에 대해서 조치되어지는 사항에 대한 책임의무를 확인합니다.',
  '개인적인 용도 또는 고의나 과실로 제 3자에 의해 사용되지 않도록 하며, 경비처리 하지 않겠습니다.',
  '법인카드 관련된 내부규정을 위반하여 발생한 모든 문제에 대한 책임은 본인에게 있습니다.',
  '카드를 분실하거나 도난 당한 경우 즉시 카드사를 통해 정지 신청하며, 분실지연에 따라 발생한 문제의 책임은 본인에게 있습니다.',
  '휴직 및 퇴사 등의 사유 발생 시 정산 후 개인형 경비카드를 즉시 회사에 반납(해지)하겠습니다.',
  '퇴사 시 미정산된 경비는 급여 보류 및 상계될 수 있음을 숙지하였습니다.',
  '본 서약서는 본인의 자발적 동의에 의하여 제출되었으며, 법인카드 발급신청서 작성과 동시에 효력을 가짐을 확인합니다.',
];

export const NEW_CARD_PLEDGE_ALERT =
  '서약서에 동의해야 품의 상신이 가능합니다.';

/** 서약서 본문 — 작성 화면과 저장 HTML 공용 */
export const NewCardPledgeText = () => (
  <>
    <strong className="pledge-title">{NEW_CARD_PLEDGE_TITLE}</strong>
    <ol className="mt-10 list-style-decimal">
      {NEW_CARD_PLEDGE_ITEMS.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ol>
  </>
);

type MarkupProps = { state: NewCorporateCardFormState; t: TFunction };

/** 레거시 #pledgeHTML 안쪽 마크업(id·class 동일) — 저장 시점 동의값을 담아 둠 */
const NewCardContentMarkup = ({ state, t }: MarkupProps) => (
  <>
    <div className="section-title">
      <strong>{t('개인형 법인카드 서약')}</strong>
    </div>
    <div className="pledge-form mt-10">
      <NewCardPledgeText />
    </div>
    <div className="agree-chk-area mt-10">
      <div className="d-flex align-items-center">
        <div className="d-flex align-items-center">
          <input
            type="radio"
            id="pledge_agree"
            name="pledge_agree"
            disabled
            defaultChecked={state.pledgeAgree === 'Y'}
          />
          <label htmlFor="pledge_agree">{t('동의함')}</label>
        </div>
        <div className="d-flex align-items-center">
          <input
            type="radio"
            id="pledge_disagree"
            name="pledge_agree"
            disabled
            defaultChecked={state.pledgeAgree === 'N'}
          />
          <label htmlFor="pledge_disagree">{t('동의하지 않음')}</label>
        </div>
      </div>
    </div>
  </>
);

/** attributes.content — 레거시는 서약 영역 HTML 만 저장 */
export const buildNewCorporateCardContentHtml = (
  state: NewCorporateCardFormState,
  t: TFunction,
): string => renderToStaticMarkup(<NewCardContentMarkup state={state} t={t} />);

const DROP_TAGS = new Set(['style', 'script', 'link']);

// 조회 전용이라 비활성이어도 본문색
const viewRadioSx = {
  justifyContent: 'flex-end',
  '& .MuiFormControlLabel-root.Mui-disabled .MuiFormControlLabel-label.Mui-disabled':
    {
      color: 'var(--dk-text, #1A1A1A)',
      fontSize: 14,
      lineHeight: '20px',
    },
};

/** 레거시 동의 라디오(.agree-chk-area) → 공통 라디오 그룹 입력값. 레거시 문서는 label 이 input 밖(for=) */
const agreeRadiosOf = (element: Element) => {
  const inputs = Array.from(
    element.querySelectorAll<HTMLInputElement>('input[type=radio]'),
  );
  if (!inputs.length) return null;
  const root = element.ownerDocument;
  return {
    checked:
      inputs.find((i) => i.hasAttribute('checked'))?.getAttribute('id') ?? '',
    options: inputs.map((i) => {
      const id = i.getAttribute('id') ?? '';
      const label =
        (id && root.querySelector(`label[for="${id}"]`)?.textContent) ||
        i.closest('label')?.textContent ||
        '';
      return { value: id, label: label.trim() };
    }),
  };
};

type SnapshotProps = { content: string; pledgeAgree: boolean };

/** 저장 content(레거시 문서 포함) 그대로 — 동의 여부는 레거시 조회처럼 저장값으로 */
const NewCardPledgeSnapshot = ({ content, pledgeAgree }: SnapshotProps) => {
  const transform = useCallback<ElementTransform>(
    ({ element, tagName, props, children }) => {
      if (DROP_TAGS.has(tagName)) return null;
      if (element.id === 'disagree-message') return null;
      const key = props.key as string;
      if (element.classList.contains('section-title')) {
        return (
          <InfoSectionTitle key={key} sx={{ mb: 1 }}>
            {element.textContent?.trim()}
          </InfoSectionTitle>
        );
      }
      if (element.classList.contains('pledge-form')) {
        return <NewCardPledgeBox key={key}>{children}</NewCardPledgeBox>;
      }
      if (element.classList.contains('agree-chk-area')) {
        const radios = agreeRadiosOf(element);
        if (!radios) return null;
        return (
          <NewCardAgreeRow key={key}>
            <RadioGroupV2
              row
              disabled
              value={pledgeAgree ? 'pledge_agree' : radios.checked}
              options={radios.options}
              sx={viewRadioSx}
            />
          </NewCardAgreeRow>
        );
      }
      return undefined;
    },
    [pledgeAgree],
  );
  return <>{useHtmlToReact(content, { transform })}</>;
};

type ViewProps = { data: NewCorporateCardAttributes };

/** 조회 — 레거시 appr_view_forms_new_card: 신청자·신청 정보는 저장값, 서약은 저장 content */
export const NewCorporateCardView = ({ data }: ViewProps) => {
  const { t } = useTranslation();
  const myPhone = useAtomValue(MyInfoState)?.phoneMobile ?? '';
  const isCardManager = useHasRoles(CORPORATE_CARD_MANAGER_ROLE_KEY, {
    redirectOnForbidden: false,
  });
  const [authOpen, setAuthOpen] = useState(false);
  const [verified, setVerified] = useState(false);

  const handleVerify = async (verification: SmsVerification) => {
    // 인증에 쓴 번호는 바로 만료(레거시 동일) — 실패해도 열람은 진행
    fetchApi({
      method: 'POST',
      url: EXPIRE_CERTIFY_CODE_URL,
      data: {
        smsVerificationId: verification.verificationId,
        smsVerificationCode: verification.verificationCode,
      },
    }).catch(() => undefined);
    setVerified(true);
    setAuthOpen(false);
  };

  // 비공개 링크는 금방 만료돼 누를 때마다 uuid 로 새로 받음 — 실패해도 다시 누르면 재시도
  const fileCell = (uuid: string | null, name: string | null) =>
    verified && uuid && name ? (
      <AuthFileLink
        href="#"
        onClick={(e) => {
          e.preventDefault();
          openDownloadUrlInNewTab(uuid);
        }}
      >
        {name}
        <NewWindowIcon width={14} height={14} />
      </AuthFileLink>
    ) : (
      <AuthFileHint>
        {t('* 담당자 인증 후 파일을 확인할 수 있어요.')}
      </AuthFileHint>
    );

  const englishName = [data.enName, data.enLastname].filter(Boolean).join(' ');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: `${APPROVAL_SECTION_GAP_LG}px`,
      }}
    >
      <Box>
        {!authOpen && (
          <AuthNotice>
            <div>
              {t('본 품의는 일부 정보가 가려져 있어요.')}
              <br />
              {t(
                '법인카드 담당자만 인증 후 문서의 모든 정보를 확인할 수 있어요.',
              )}
            </div>
            {isCardManager && (
              <BoxButton
                variant="secondary"
                size="medium"
                disabled={verified}
                onClick={() => setAuthOpen(true)}
              >
                {verified ? t('인증완료') : t('인증')}
              </BoxButton>
            )}
          </AuthNotice>
        )}
        {authOpen && (
          <Box>
            <InfoSectionTitle sx={{ mb: 2 }}>
              {t('담당자 인증')}
            </InfoSectionTitle>
            <SmsVerificationForm
              phoneNumber={formatNewCardPhoneNumber(myPhone)}
              onVerify={handleVerify}
            />
          </Box>
        )}
      </Box>

      <Box>
        <InfoSectionTitle sx={{ mb: 1 }}>{t('신청자 정보')}</InfoSectionTitle>
        <InfoList
          labelWidth={110}
          rows={[
            { label: t('이름'), value: data.employeeName },
            { label: t('사번'), value: data.employeeNumber },
            { label: t('소속'), value: data.department ?? '' },
            { label: t('이메일'), value: data.email },
            { label: t('입사일'), value: data.hireDate },
          ]}
        />
      </Box>

      <Box>
        <InfoSectionTitle sx={{ mb: 1 }}>{t('신청 정보')}</InfoSectionTitle>
        <InfoList
          labelWidth={110}
          rows={[
            {
              label: t('신분증'),
              value: fileCell(data.idCardUuid, data.idCardFilename),
            },
            {
              label: t('급여 통장사본'),
              value: fileCell(data.bankBookUuid, data.bankBookFilename),
            },
            {
              label: t('신용 정보 조회 동의서'),
              value: data.creditInfoConsent ? t('제출 완료') : t('미제출'),
            },
            { label: t('영문 이름'), value: englishName },
            { label: t('SMS 수신 번호'), value: data.phoneNumber ?? '' },
            {
              label: t('이용한도'),
              value: newCardCreditLimitLabel(data.creditLimit),
            },
            { label: t('카드 종류'), value: data.cardType ?? '' },
          ]}
        />
      </Box>

      {data.content ? (
        <Box>
          <NewCardPledgeSnapshot
            content={data.content}
            pledgeAgree={data.pledgeAgree}
          />
        </Box>
      ) : null}
    </Box>
  );
};
